import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,

  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import StarIcon from '@mui/icons-material/Star';
import BoltIcon from '@mui/icons-material/Bolt';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EastIcon from '@mui/icons-material/East';
import LoopIcon from '@mui/icons-material/Loop';
import TripOriginIcon from '@mui/icons-material/TripOrigin';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import RideCard from '../components/UI/RideCard';
import { DriverRidePreferences, FilterPreset, PublicRide, RidesQuery, RidesSortBy } from '../types';
import { fetchPublicRides } from '../services/publicApi';
import { fetchDriverRides, fetchDriverRideById } from '../services/driverRideService';
import { hideLead, reportLead, saveLead, unsaveLead, unhideLead } from '../services/driverLeadService';
import { fetchLeadEntitlements, fetchMyUnlocks, unlockLead, LeadEntitlements } from '../services/leadAccessService';
import {
  defaultRidePreferences,
  fetchRidePreferences,
} from '../services/preferencesService';
import {
  createFilterPreset,
  fetchFilterPresets,
  updateFilterPreset,
} from '../services/filterPresetService';
import { matchRideToPreferences } from '../utils/rideMatching';
import { suggestCities } from '../utils/cities';
import { tidyOriginalMessage } from '../utils/messageDisplay';
import { useAuth } from '../contexts/AuthContext';
import { getSocket } from '../services/socketService';

type FeedMode = 'personalized' | 'all';
type RideTab = 'best' | 'all' | 'saved' | 'hidden' | 'unlocked';
type ViewMode = 'grid' | 'list';

const splitCsv = (value: string) => value.split(',').map((item) => item.trim()).filter(Boolean);

const LIMIT = 20;
const LAST_FILTER_PRESET_KEY = 'rides_last_filter_preset_id';

const readLastFilterPresetId = () => {
  try {
    return window.localStorage.getItem(LAST_FILTER_PRESET_KEY) || '';
  } catch {
    return '';
  }
};

const rememberLastFilterPresetId = (presetId: string) => {
  try {
    if (presetId) {
      window.localStorage.setItem(LAST_FILTER_PRESET_KEY, presetId);
    } else {
      window.localStorage.removeItem(LAST_FILTER_PRESET_KEY);
    }
  } catch {
    // localStorage is best-effort only.
  }
};

const hasFilterParams = (params: URLSearchParams) =>
  ['search', 'vehicleTypes', 'dutyTypes', 'priorities', 'pickupAreas', 'dropAreas', 'urgentOnly']
    .some((key) => Boolean(params.get(key)));

const VEHICLE_TYPES = [
  { value: 'hatchback', label: 'Hatchback', sub: 'Alto, i10, Swift, Wagon R, Baleno…' },
  { value: 'sedan', label: 'Sedan', sub: 'Dzire, Aura, City, Verna, Amaze…' },
  { value: 'suv', label: 'SUV', sub: 'Creta, Seltos, Brezza, Nexon, Thar…' },
  { value: 'innova', label: 'Innova / Crysta', sub: 'Innova, Crysta, Hycross…' },
  { value: 'ertiga', label: 'Ertiga / MUV', sub: 'Ertiga, XL6, Marazzo…' },
  { value: 'fortuner', label: 'Fortuner / Large SUV', sub: 'Fortuner, Scorpio, Bolero, Safari…' },
  { value: 'tempo', label: 'Tempo Traveller', sub: '9–17 seater, Urbania…' },
  { value: 'bus', label: 'Bus', sub: 'Mini bus, 20+ seater…' },
  { value: 'luxury', label: 'Luxury', sub: 'BMW, Mercedes, Audi, Camry…' },
];

const DUTY_TYPES = [
  { value: 'One Way', label: 'One Way' },
  { value: 'Round Trip', label: 'Round Trip' },
  { value: 'Local', label: 'Local' },
  { value: 'Up & Down', label: 'Up & Down' },
  { value: 'Airport', label: 'Airport' },
  { value: 'Outstation', label: 'Outstation' },
  { value: 'Exchange Duty', label: 'Exchange Duty' },
];


const PRIORITY_OPTIONS: Array<{ value: 'high' | 'normal' | 'low'; label: string }> = [
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

const SORT_OPTIONS: Array<{ value: RidesSortBy; label: string }> = [
  { value: 'newest', label: 'Newest first' },
  { value: 'priority', label: 'Priority' },
  { value: 'match', label: 'Best match' },
];

function isUrgentRide(dateStr: string): boolean {
  return Date.now() - new Date(dateStr).getTime() < 20 * 60 * 1000;
}

function isRecentRide(dateStr: string): boolean {
  return Date.now() - new Date(dateStr).getTime() < 60 * 60 * 1000;
}

interface PublicRidesProps {
  freeMode?: boolean;
}

const PublicRides: React.FC<PublicRidesProps> = ({ freeMode = false }) => {
  const { token, user } = useAuth();
  const isVerified = !token || user?.emailVerified !== false;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const inDashboard = pathname === '/app/rides';

  const [searchParams, setSearchParams] = useSearchParams();
  const initialUrlHasFilterParamsRef = useRef(hasFilterParams(searchParams));
  const initialPresetIdRef = useRef(searchParams.get('filterPresetId') || (!initialUrlHasFilterParamsRef.current ? readLastFilterPresetId() : ''));
  const shouldApplyInitialPresetRef = useRef(Boolean(initialPresetIdRef.current) && !initialUrlHasFilterParamsRef.current);
  const didApplyInitialPresetRef = useRef(false);

  // Seed filter state from URL params so reloads (and shared filtered URLs)
  // restore the same view. Lists are stored as comma-separated values.
  const parseParamList = (key: string) =>
    splitCsv(searchParams.get(key) || '');
  const parseSortBy = (raw: string | null): RidesSortBy =>
    raw === 'priority' || raw === 'match' ? raw : 'newest';

  const [rides, setRides] = useState<PublicRide[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [vehicleFilters, setVehicleFilters] = useState<string[]>(parseParamList('vehicleTypes'));
  const [dutyTypes, setDutyTypes] = useState<string[]>(parseParamList('dutyTypes'));
  const [priorities, setPriorities] = useState<Array<'low' | 'normal' | 'high'>>(
    parseParamList('priorities').filter((v): v is 'low' | 'normal' | 'high' => v === 'low' || v === 'normal' || v === 'high'),
  );
  const [pickupAreas, setPickupAreas] = useState<string[]>(parseParamList('pickupAreas'));
  const [dropAreas, setDropAreas] = useState<string[]>(parseParamList('dropAreas'));
  const [urgentOnlyFilter, setUrgentOnlyFilter] = useState(searchParams.get('urgentOnly') === 'true');
  const [sortBy, setSortBy] = useState<RidesSortBy>(parseSortBy(searchParams.get('sortBy')));
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [rideTab, setRideTab] = useState<RideTab>('best');
  const [feedMode, setFeedMode] = useState<FeedMode>('personalized');
  const [preferences, setPreferences] = useState<DriverRidePreferences>(defaultRidePreferences);
  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [savingPreset, setSavingPreset] = useState(false);
  const [updatingPreset, setUpdatingPreset] = useState(false);
  const [entitlements, setEntitlements] = useState<LeadEntitlements | null>(null);
  const [unlocked, setUnlocked] = useState<Record<string, { price?: string; contactName?: string; contactNumber?: string; originalMessage?: string }>>({});
  const [unlockingId, setUnlockingId] = useState<string | null>(null);
  const [actionWorkingId, setActionWorkingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedRide, setSelectedRide] = useState<PublicRide | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [savePresetName, setSavePresetName] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState(initialPresetIdRef.current);
  const [onlyRecentFilter, setOnlyRecentFilter] = useState(false);
  const [hideExpiredFilter, setHideExpiredFilter] = useState(false);
  const [showExchangeDutyFilter, setShowExchangeDutyFilter] = useState(
    searchParams.get('showExchangeDuty') === 'true',
  );
  const [showSupplyFilter, setShowSupplyFilter] = useState(
    searchParams.get('showSupply') === 'true',
  );
  const [focusedRouteField, setFocusedRouteField] = useState<'pickup' | 'drop' | null>(null);
  const [showMoreCities, setShowMoreCities] = useState(false);
  const [showVehicleInput, setShowVehicleInput] = useState(false);
  const [showTripInput, setShowTripInput] = useState(false);

  const [totalRides, setTotalRides] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const pageRef = useRef(1);

  const searchRef = useRef(searchParams.get('search') || '');
  const vehicleRef = useRef<string[]>(parseParamList('vehicleTypes'));
  const dutyRef = useRef<string[]>(parseParamList('dutyTypes'));
  const priorityRef = useRef<Array<'low' | 'normal' | 'high'>>([]);
  const pickupAreasRef = useRef<string[]>(parseParamList('pickupAreas'));
  const dropAreasRef = useRef<string[]>(parseParamList('dropAreas'));
  const sortByRef = useRef<RidesSortBy>(parseSortBy(searchParams.get('sortBy')));
  const showSupplyRef = useRef<boolean>(searchParams.get('showSupply') === 'true');
  const rideTabRef = useRef<RideTab>('best');
  const hasMoreRef = useRef(false);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const routeBlurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Core fetch ─────────────────────────────────────────────────────────────
  type LoadFilters = {
    search: string;
    tab: RideTab;
    vehicleTypes: string[];
    dutyTypes: string[];
    priorities: Array<'low' | 'normal' | 'high'>;
    pickupAreas: string[];
    dropAreas: string[];
    sortBy: RidesSortBy;
    showSupply: boolean;
  };

  const loadRides = async (
    filters: LoadFilters,
    currentPage: number,
    append = false,
  ) => {
    const { search: searchValue, tab, vehicleTypes, dutyTypes: dutyValues, priorities: priorityValues, pickupAreas: pickup, dropAreas: drop, sortBy: sortValue, showSupply: showSupplyValue } = filters;
    if (append) {
      setLoadingMore(true);
      loadingMoreRef.current = true;
    } else {
      setLoading(true);
      loadingRef.current = true;
      if (currentPage === 1) setPendingCount(0);
    }
    setLoadError(null);
    try {
      if (token && tab === 'unlocked') {
        const response = await fetchMyUnlocks(currentPage, LIMIT);
        const nextUnlocked: Record<string, { price?: string; contactName?: string; contactNumber?: string; originalMessage?: string }> = {};
        const unlockedRides: PublicRide[] = response.unlocks.map((item) => {
          nextUnlocked[item.ride.id] = {
            price: item.ride.price,
            contactName: item.ride.contactName,
            contactNumber: item.ride.contactNumber,
            originalMessage: item.ride.originalMessage,
          };
          return {
            id: item.ride.id,
            from: item.ride.from,
            to: item.ride.to,
            time: item.ride.time,
            price: item.ride.price,
            confidence: 0,
            priority: item.ride.priority,
            status: item.ride.status,
            createdAt: item.ride.createdAt,
            sensitiveHidden: false,
          };
        });
        setUnlocked((prev) => ({ ...prev, ...nextUnlocked }));
        setRides((prev) => append ? [...prev, ...unlockedRides] : unlockedRides);
        setTotalRides(response.pagination?.total ?? unlockedRides.length);
        const more = currentPage < (response.pagination?.pages ?? 1);
        setHasMore(more);
        hasMoreRef.current = more;
        return;
      }

      const response = token
        ? await fetchDriverRides({
          search: searchValue || undefined,
          vehicleTypes,
          dutyTypes: dutyValues,
          priorities: priorityValues,
          pickupAreas: pickup.length ? pickup : undefined,
          dropAreas: drop.length ? drop : undefined,
          sortBy: sortValue,
          saved: tab === 'saved' || undefined,
          hidden: tab === 'hidden' || undefined,
          includeHidden: tab === 'hidden' || undefined,
          showSupply: showSupplyValue || undefined,
          page: currentPage,
          limit: LIMIT,
        })
        : await fetchPublicRides({
          search: searchValue || undefined,
          vehicleTypes,
          dutyTypes: dutyValues,
          priorities: priorityValues,
          pickupAreas: pickup.length ? pickup : undefined,
          dropAreas: drop.length ? drop : undefined,
          showSupply: showSupplyValue || undefined,
          page: currentPage,
          limit: LIMIT,
        });
      if (!token && freeMode) {
        const freeContacts = response.rides.reduce<Record<string, {
          price?: string;
          contactName?: string;
          contactNumber?: string;
          originalMessage?: string;
        }>>((map, ride) => {
          map[ride.id] = {
            price: ride.price,
            contactName: ride.contactName,
            contactNumber: ride.contactNumber,
            originalMessage: ride.originalMessage,
          };
          return map;
        }, {});
        setUnlocked((prev) => append ? { ...prev, ...freeContacts } : freeContacts);
      }
      setRides((prev) => append ? [...prev, ...response.rides] : response.rides);
      setTotalRides(response.pagination?.total ?? response.rides.length);
      const more = currentPage < (response.pagination?.pages ?? 1);
      setHasMore(more);
      hasMoreRef.current = more;
    } catch (err: any) {
      if (!append) setRides([]);
      setLoadError(err?.response?.data?.message || 'Failed to load rides. Please try again.');
    } finally {
      if (append) {
        setLoadingMore(false);
        loadingMoreRef.current = false;
      } else {
        setLoading(false);
        loadingRef.current = false;
      }
    }
  };

  const currentFilters = (overrides: Partial<LoadFilters> = {}): LoadFilters => ({
    search: overrides.search ?? searchRef.current,
    tab: overrides.tab ?? rideTabRef.current,
    vehicleTypes: overrides.vehicleTypes ?? vehicleRef.current,
    dutyTypes: overrides.dutyTypes ?? dutyRef.current,
    priorities: overrides.priorities ?? priorityRef.current,
    pickupAreas: overrides.pickupAreas ?? pickupAreasRef.current,
    dropAreas: overrides.dropAreas ?? dropAreasRef.current,
    sortBy: overrides.sortBy ?? sortByRef.current,
    showSupply: overrides.showSupply ?? showSupplyRef.current,
  });

  const resetAndLoad = (overrides: Partial<LoadFilters> = {}) => {
    pageRef.current = 1;
    loadRides(currentFilters(overrides), 1, false);
  };

  useEffect(() => {
    if (!token) {
      setEntitlements(null);
      setFeedMode('all');
      const nextTab: RideTab = 'all';
      setRideTab(nextTab);
      rideTabRef.current = nextTab;
      pageRef.current = 1;
      loadRides(currentFilters({ tab: 'all' }), 1, false);
      return;
    }
    const nextTab: RideTab = 'best';
    setFeedMode('personalized');
    setRideTab(nextTab);
    rideTabRef.current = nextTab;
    pageRef.current = 1;
    loadRides(currentFilters({ tab: nextTab }), 1, false);
    Promise.all([
      fetchLeadEntitlements().catch(() => null),
      fetchRidePreferences().catch(() => defaultRidePreferences),
      fetchMyUnlocks(1, 50).catch(() => null),
      fetchFilterPresets().catch(() => [] as FilterPreset[]),
    ]).then(([ent, prefs, unlockHistory, presetList]) => {
      setEntitlements(ent);
      setPreferences(prefs);
      setPresets(presetList);
      if (unlockHistory) {
        const map: Record<string, { price?: string; contactName?: string; contactNumber?: string; originalMessage?: string }> = {};
        unlockHistory.unlocks.forEach((item) => {
          map[item.ride.id] = {
            price: item.ride.price,
            contactName: item.ride.contactName,
            contactNumber: item.ride.contactNumber,
            originalMessage: item.ride.originalMessage,
          };
        });
        setUnlocked(map);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Sync filter state → refs (for stale-closure-safe pagination) and → URL.
  useEffect(() => {
    searchRef.current = search;
    vehicleRef.current = vehicleFilters;
    dutyRef.current = dutyTypes;
    priorityRef.current = priorities;
    pickupAreasRef.current = pickupAreas;
    dropAreasRef.current = dropAreas;
    sortByRef.current = sortBy;
    showSupplyRef.current = showSupplyFilter;

    // Push to URL (debounced together with the fetch below).
    const next = new URLSearchParams();
    if (search) next.set('search', search);
    if (vehicleFilters.length) next.set('vehicleTypes', vehicleFilters.join(','));
    if (dutyTypes.length) next.set('dutyTypes', dutyTypes.join(','));
    if (priorities.length) next.set('priorities', priorities.join(','));
    if (pickupAreas.length) next.set('pickupAreas', pickupAreas.join(','));
    if (dropAreas.length) next.set('dropAreas', dropAreas.join(','));
    if (urgentOnlyFilter) next.set('urgentOnly', 'true');
    if (showExchangeDutyFilter) next.set('showExchangeDuty', 'true');
    if (showSupplyFilter) next.set('showSupply', 'true');
    if (selectedPresetId) next.set('filterPresetId', selectedPresetId);
    if (sortBy !== 'newest') next.set('sortBy', sortBy);
    setSearchParams(next, { replace: true });

    const timer = setTimeout(() => {
      resetAndLoad();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, vehicleFilters, dutyTypes, priorities, pickupAreas, dropAreas, sortBy, urgentOnlyFilter, showExchangeDutyFilter, showSupplyFilter, selectedPresetId]);

  useEffect(() => {
    if (
      !token ||
      didApplyInitialPresetRef.current ||
      !shouldApplyInitialPresetRef.current ||
      !selectedPresetId ||
      presets.length === 0
    ) return;
    const preset = presets.find((item) => item.id === selectedPresetId);
    didApplyInitialPresetRef.current = true;
    shouldApplyInitialPresetRef.current = false;
    if (preset) {
      applyPresetQuery(preset.query);
      rememberLastFilterPresetId(preset.id);
      return;
    }
    rememberLastFilterPresetId('');
    setSelectedPresetId('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, presets, selectedPresetId]);

  useEffect(() => {
    const socket = getSocket();
    const handleNewRide = () => setPendingCount((n) => n + 1);
    socket.on('newRide', handleNewRide);
    socket.on('rideUpdate', handleNewRide);
    return () => {
      socket.off('newRide', handleNewRide);
      socket.off('rideUpdate', handleNewRide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link from notification bell: ?openRide=ID
  const openRideIdParam = searchParams.get('openRide');
  const openRideAttemptedRef = useRef(false);
  useEffect(() => {
    if (!openRideIdParam || loading || openRideAttemptedRef.current) return;
    const inList = rides.find((r) => r.id === openRideIdParam);
    if (inList) {
      openRideAttemptedRef.current = true;
      setSelectedRide(inList);
      setSearchParams((prev) => { prev.delete('openRide'); return prev; }, { replace: true });
      return;
    }
    if (!token) return;
    // Ride not in current page — fetch directly
    openRideAttemptedRef.current = true;
    fetchDriverRideById(openRideIdParam)
      .then((ride) => {
        setSelectedRide(ride);
        setSearchParams((prev) => { prev.delete('openRide'); return prev; }, { replace: true });
      })
      .catch(() => {
        // Ride may be expired/inaccessible — silently ignore
        setSearchParams((prev) => { prev.delete('openRide'); return prev; }, { replace: true });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRideIdParam, loading, rides]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMoreRef.current && !loadingRef.current && !loadingMoreRef.current) {
        const next = pageRef.current + 1;
        pageRef.current = next;
        loadRides(currentFilters(), next, true);
      }
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeRideTab = (_: React.SyntheticEvent, next: RideTab) => {
    setRideTab(next);
    rideTabRef.current = next;
    setFeedMode(next === 'best' ? 'personalized' : 'all');
    resetAndLoad({ tab: next });
  };

  const matchedRides = useMemo(() => {
    const withMatches = rides.map((ride) => (
      token ? { ...ride, match: matchRideToPreferences(ride, preferences) } : ride
    ));
    if (!token || feedMode === 'all') {
      return withMatches.sort((a, b) => Number(new Date(b.createdAt)) - Number(new Date(a.createdAt)));
    }
    return withMatches.sort((a, b) => {
      const aM = a.match || { isMatch: false, score: 0 };
      const bM = b.match || { isMatch: false, score: 0 };
      if (aM.isMatch !== bM.isMatch) return aM.isMatch ? -1 : 1;
      if (aM.score !== bM.score) return bM.score - aM.score;
      return Number(new Date(b.createdAt)) - Number(new Date(a.createdAt));
    });
  }, [feedMode, preferences, rides, token]);

  const urgentCount = useMemo(
    () => matchedRides.filter((r) => isUrgentRide(r.createdAt)).length,
    [matchedRides],
  );

  const displayedRides = useMemo(() => {
    // Server handles vehicle/duty/priority/area/sortBy.
    // Time-based and status-based filters run client-side.
    let result = matchedRides;
    if (urgentOnlyFilter) result = result.filter((r) => isUrgentRide(r.createdAt));
    if (onlyRecentFilter) result = result.filter((r) => isRecentRide(r.createdAt));
    if (hideExpiredFilter) result = result.filter((r) => r.status !== 'expired');
    if (!showExchangeDutyFilter) result = result.filter((r) => (r.dutyType || '').toLowerCase() !== 'exchange duty');
    if (!showSupplyFilter) result = result.filter((r) => (r.leadType || 'demand') !== 'supply');
    if (sortBy === 'newest') {
      result = [...result].sort((a, b) => Number(new Date(b.createdAt)) - Number(new Date(a.createdAt)));
    } else if (sortBy === 'match') {
      result = [...result].sort((a, b) => {
        const aM = a.match || { isMatch: false, score: 0 };
        const bM = b.match || { isMatch: false, score: 0 };
        if (aM.isMatch !== bM.isMatch) return aM.isMatch ? -1 : 1;
        return bM.score - aM.score;
      });
    }
    return result;
  }, [matchedRides, urgentOnlyFilter, onlyRecentFilter, hideExpiredFilter, showExchangeDutyFilter, showSupplyFilter, sortBy]);

  const personalizedCount = matchedRides.filter((r) => r.match?.isMatch).length;
  const hasActiveFilters = !!(
    search
    || vehicleFilters.length
    || dutyTypes.length
    || priorities.length
    || pickupAreas.length
    || dropAreas.length
    || urgentOnlyFilter
    || onlyRecentFilter
    || hideExpiredFilter
    || showExchangeDutyFilter
    || showSupplyFilter
  );

  const clearAllFilters = () => {
    setSearch('');
    setVehicleFilters([]);
    setDutyTypes([]);
    setPriorities([]);
    setPickupAreas([]);
    setDropAreas([]);
    setUrgentOnlyFilter(false);
    setOnlyRecentFilter(false);
    setHideExpiredFilter(false);
    setShowExchangeDutyFilter(false);
    setShowSupplyFilter(false);
    setShowMoreCities(false);
    setSelectedPresetId('');
    rememberLastFilterPresetId('');
  };

  const toggleInList = <T,>(list: T[], value: T) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  // ── Filter presets ─────────────────────────────────────────────────────────
  const buildCurrentQuery = (): Partial<RidesQuery> => ({
    vehicleTypes: vehicleFilters,
    dutyTypes,
    priorities,
    pickupAreas,
    dropAreas,
    search,
    urgentOnly: urgentOnlyFilter,
    onlyRecent: onlyRecentFilter || undefined,
    hideExpired: hideExpiredFilter || undefined,
    showExchangeDuty: showExchangeDutyFilter || undefined,
    showSupply: showSupplyFilter || undefined,
  });

  const applyPresetQuery = (query: Partial<RidesQuery>) => {
    setSearch(query.search ?? '');
    setVehicleFilters(query.vehicleTypes ?? []);
    setDutyTypes(query.dutyTypes ?? []);
    setPriorities((query.priorities ?? []) as Array<'low' | 'normal' | 'high'>);
    setPickupAreas(query.pickupAreas ?? []);
    setDropAreas(query.dropAreas ?? []);
    setUrgentOnlyFilter(query.urgentOnly ?? false);
    setOnlyRecentFilter(query.onlyRecent ?? false);
    setHideExpiredFilter(query.hideExpired ?? false);
    setShowExchangeDutyFilter(query.showExchangeDuty ?? false);
    setShowSupplyFilter(query.showSupply ?? false);
  };

  const handleSelectPreset = (presetId: string) => {
    if (!presetId) {
      clearAllFilters();
      setSavePresetName('');
      return;
    }
    setSelectedPresetId(presetId);
    rememberLastFilterPresetId(presetId);
    const preset = presets.find((p) => p.id === presetId);
    if (preset) {
      applyPresetQuery(preset.query);
      setSavePresetName(preset.name);
    }
  };

  // Unified save: updates selected preset (name + filters) or creates new one.
  const handleSaveFilter = async () => {
    const name = savePresetName.trim();
    if (!name) return;
    if (selectedPreset) {
      setUpdatingPreset(true);
      try {
        const updated = await updateFilterPreset(selectedPreset.id, { name, query: buildCurrentQuery() });
        setPresets((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        setSelectedPresetId(updated.id);
        rememberLastFilterPresetId(updated.id);
        setSavePresetName(updated.name);
        setMessage(`Filter "${updated.name}" updated.`);
      } catch (err: any) {
        setMessage(err?.response?.data?.message || 'Could not update filter.');
      } finally {
        setUpdatingPreset(false);
      }
    } else {
      setSavingPreset(true);
      try {
        const preset = await createFilterPreset(name, buildCurrentQuery());
        setPresets((prev) => [preset, ...prev.filter((p) => p.id !== preset.id)]);
        setSelectedPresetId(preset.id);
        rememberLastFilterPresetId(preset.id);
        setSavePresetName(preset.name);
        setMessage(`Filter "${preset.name}" saved.`);
      } catch (err: any) {
        setMessage(err?.response?.data?.message || 'Could not save filter.');
      } finally {
        setSavingPreset(false);
      }
    }
  };

  const applyMyPreferences = () => {
    if (preferences.vehicleTypes.length) setVehicleFilters(preferences.vehicleTypes);
    if (preferences.pickupKeywords.length) setPickupAreas(preferences.pickupKeywords);
    if (preferences.dropoffKeywords.length) setDropAreas(preferences.dropoffKeywords);
    setMessage('Filters filled from your preferences.');
  };

  const handleSaveLead = async (rideId: string) => {
    setActionWorkingId(rideId);
    try {
      await saveLead(rideId);
      setRides((prev) => prev.map((r) => r.id === rideId ? { ...r, saved: true } : r));
      setMessage('Ride saved.');
    } catch { setMessage('Unable to save ride.'); }
    finally { setActionWorkingId(null); }
  };

  const handleUnsaveLead = async (rideId: string) => {
    setActionWorkingId(rideId);
    try {
      await unsaveLead(rideId);
      if (rideTabRef.current === 'saved') {
        setRides((prev) => prev.filter((r) => r.id !== rideId));
      } else {
        setRides((prev) => prev.map((r) => r.id === rideId ? { ...r, saved: false } : r));
      }
      setMessage('Ride unsaved.');
    } catch { setMessage('Unable to unsave ride.'); }
    finally { setActionWorkingId(null); }
  };

  const handleHideLead = async (rideId: string) => {
    setActionWorkingId(rideId);
    try {
      await hideLead(rideId);
      setRides((prev) => prev.filter((r) => r.id !== rideId));
      setMessage('Ride hidden.');
    } catch { setMessage('Unable to hide ride.'); }
    finally { setActionWorkingId(null); }
  };

  const handleUnhideLead = async (rideId: string) => {
    setActionWorkingId(rideId);
    try {
      await unhideLead(rideId);
      setRides((prev) => prev.filter((r) => r.id !== rideId));
      setMessage('Ride unhidden.');
    } catch { setMessage('Unable to unhide ride.'); }
    finally { setActionWorkingId(null); }
  };

  const handleReportLead = async (rideId: string) => {
    setActionWorkingId(rideId);
    try {
      await reportLead(rideId, 'not_relevant');
      setRides((prev) => prev.map((r) => r.id === rideId ? { ...r, reported: true } : r));
      setMessage('Thanks. This ride was marked not relevant.');
    } catch { setMessage('Unable to report ride.'); }
    finally { setActionWorkingId(null); }
  };

  const handleUnlockLead = async (ride: PublicRide) => {
    if (!token) { navigate('/signup'); return; }
    try {
      setUnlockingId(ride.id);
      const result = await unlockLead(ride.id);
      setUnlocked((prev) => ({
        ...prev,
        [ride.id]: {
          price: result.price,
          contactName: result.contactName,
          contactNumber: result.contactNumber,
          originalMessage: result.originalMessage,
        },
      }));
      setEntitlements(result.entitlements);
      setMessage('Ride Unlocked! Full ride details are now available.');
    } catch (error: any) {
      const status = error?.response?.status;
      const serverMsg = error?.response?.data?.message;
      if (serverMsg) setMessage(serverMsg);
      else if (status === 402 || status === 403) setMessage('No credits left. Top up your wallet.');
      else setMessage('Unable to unlock ride. Please try again.');
    } finally {
      setUnlockingId(null);
    }
  };

  const handleLoadNew = () => {
    pageRef.current = 1;
    setPendingCount(0);
    loadRides(currentFilters(), 1, false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Filter sidebar content ─────────────────────────────────────────────────
  const [vehicleCustomInput, setVehicleCustomInput] = useState('');
  const [dutyCustomInput, setDutyCustomInput] = useState('');
  const [pickupCityInput, setPickupCityInput] = useState('');
  const [dropCityInput, setDropCityInput] = useState('');

  const addCustomVehicle = () => {
    const v = vehicleCustomInput.trim();
    if (v && !vehicleFilters.includes(v)) setVehicleFilters([...vehicleFilters, v]);
    setVehicleCustomInput('');
  };
  const addCustomDuty = () => {
    const v = dutyCustomInput.trim();
    if (v && !dutyTypes.includes(v)) setDutyTypes([...dutyTypes, v]);
    setDutyCustomInput('');
  };
  const addPickupCity = (city: string) => {
    const c = city.trim();
    if (c && !pickupAreas.includes(c)) setPickupAreas([...pickupAreas, c]);
    setPickupCityInput('');
  };
  const addDropCity = (city: string) => {
    const c = city.trim();
    if (c && !dropAreas.includes(c)) setDropAreas([...dropAreas, c]);
    setDropCityInput('');
  };

  const filteredPickupSuggestions = suggestCities(pickupCityInput, pickupAreas, 12);
  const filteredDropSuggestions = suggestCities(dropCityInput, dropAreas, 12);
  const selectedPreset = presets.find((preset) => preset.id === selectedPresetId);

  const popularCitySuggestions = useMemo(
    () => suggestCities('', [...pickupAreas, ...dropAreas], showMoreCities ? 20 : 8),
    [pickupAreas, dropAreas, showMoreCities],
  );

  const handlePopularCityClick = (city: string) => {
    if (focusedRouteField === 'drop') addDropCity(city);
    else addPickupCity(city);
  };

  const filterContent = (
    <Stack spacing={0} divider={<Divider />}>

      {/* ── Keyword search ──────────────────────────────────────────────── */}
      <Box py={1.5}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by pickup, drop, vehicle, message…"
          size="small"
          fullWidth
          sx={{ '& input': { fontSize: 12 } }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fontSize: 16, color: 'text.disabled', mr: 0.5 }} />,
            endAdornment: search ? (
              <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.25 }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            ) : undefined,
          }}
        />
      </Box>

      {/* ── Quick filters ─────────────────────────────────────────────── */}
      <Box py={1.5}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2" fontWeight={700}>Quick filters</Typography>
          {token && (preferences.pickupKeywords.length > 0 || preferences.vehicleTypes.length > 0) && (
            <Button size="small" variant="text" onClick={applyMyPreferences}
              sx={{ fontSize: 11, py: 0, px: 0.5, color: 'text.secondary', minWidth: 0 }}>
              My preferences
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {([
            { id: 'urgent', label: 'Urgent', icon: <BoltIcon sx={{ fontSize: 13 }} />, active: urgentOnlyFilter, toggle: () => setUrgentOnlyFilter(!urgentOnlyFilter) },
            { id: 'airport', label: 'Airport', icon: <FlightTakeoffIcon sx={{ fontSize: 13 }} />, active: dutyTypes.includes('Airport'), toggle: () => setDutyTypes(toggleInList(dutyTypes, 'Airport')) },
            { id: 'local', label: 'Local', icon: <LocationOnIcon sx={{ fontSize: 13 }} />, active: dutyTypes.includes('Local'), toggle: () => setDutyTypes(toggleInList(dutyTypes, 'Local')) },
            { id: 'outstation', label: 'Outstation', icon: <EastIcon sx={{ fontSize: 13 }} />, active: dutyTypes.includes('Outstation'), toggle: () => setDutyTypes(toggleInList(dutyTypes, 'Outstation')) },
            { id: 'oneway', label: 'One Way', icon: <EastIcon sx={{ fontSize: 13 }} />, active: dutyTypes.includes('One Way'), toggle: () => setDutyTypes(toggleInList(dutyTypes, 'One Way')) },
            { id: 'roundtrip', label: 'Round Trip', icon: <LoopIcon sx={{ fontSize: 13 }} />, active: dutyTypes.includes('Round Trip'), toggle: () => setDutyTypes(toggleInList(dutyTypes, 'Round Trip')) },
          ] as Array<{ id: string; label: string; icon: React.ReactNode; active: boolean; toggle: () => void }>).map((qf) => (
            <Chip
              key={qf.id}
              label={qf.label}
              icon={qf.icon as React.ReactElement}
              size="small"
              onClick={qf.toggle}
              color={qf.active ? 'primary' : 'default'}
              variant={qf.active ? 'filled' : 'outlined'}
              sx={{ mb: 0.5, fontSize: 12 }}
            />
          ))}
        </Stack>
      </Box>

      {/* ── Route ─────────────────────────────────────────────────────── */}
      <Box py={1.5}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.25}>Route</Typography>
        <Box display="flex" gap={1.25} alignItems="flex-start">
          {/* Visual route line */}
          <Box display="flex" flexDirection="column" alignItems="center" sx={{ pt: 0.75, minWidth: 18 }}>
            <TripOriginIcon sx={{ fontSize: 14, color: '#2E7D32' }} />
            <Box sx={{ width: 0, flex: 1, minHeight: 28, borderLeft: '2px dashed rgba(0,0,0,0.18)' }} />
            <FmdGoodIcon sx={{ fontSize: 16, color: '#C62828' }} />
          </Box>
          {/* Fields */}
          <Box flex={1} minWidth={0}>
            {pickupAreas.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mb={0.5}>
                {pickupAreas.map((city) => (
                  <Chip key={city} label={city} size="small" color="primary"
                    onDelete={() => setPickupAreas(pickupAreas.filter((c) => c !== city))} />
                ))}
              </Stack>
            )}
            <TextField
              value={pickupCityInput}
              onChange={(e) => setPickupCityInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addPickupCity(pickupCityInput)}
              onFocus={() => { if (routeBlurTimer.current) clearTimeout(routeBlurTimer.current); setFocusedRouteField('pickup'); }}
              onBlur={() => { routeBlurTimer.current = setTimeout(() => setFocusedRouteField(null), 200); }}
              placeholder="Search pickup city"
              size="small"
              fullWidth
              sx={{ mb: 1, '& input': { fontSize: 12 } }}
              InputProps={{
                endAdornment: pickupCityInput
                  ? <Button size="small" sx={{ minWidth: 0, px: 1 }} onClick={() => addPickupCity(pickupCityInput)}>Add</Button>
                  : <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />,
              }}
            />
            {dropAreas.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mb={0.5}>
                {dropAreas.map((city) => (
                  <Chip key={city} label={city} size="small" color="primary"
                    onDelete={() => setDropAreas(dropAreas.filter((c) => c !== city))} />
                ))}
              </Stack>
            )}
            <TextField
              value={dropCityInput}
              onChange={(e) => setDropCityInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addDropCity(dropCityInput)}
              onFocus={() => { if (routeBlurTimer.current) clearTimeout(routeBlurTimer.current); setFocusedRouteField('drop'); }}
              onBlur={() => { routeBlurTimer.current = setTimeout(() => setFocusedRouteField(null), 200); }}
              placeholder="Search drop city"
              size="small"
              fullWidth
              sx={{ '& input': { fontSize: 12 } }}
              InputProps={{
                endAdornment: dropCityInput
                  ? <Button size="small" sx={{ minWidth: 0, px: 1 }} onClick={() => addDropCity(dropCityInput)}>Add</Button>
                  : <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />,
              }}
            />
          </Box>
        </Box>

        {/* Autocomplete suggestions */}
        {pickupCityInput && filteredPickupSuggestions.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={0.75}>
            {filteredPickupSuggestions.map((city) => (
              <Chip key={city} label={city} size="small" variant="outlined"
                onClick={() => addPickupCity(city)} sx={{ mb: 0.25 }} />
            ))}
          </Stack>
        )}
        {dropCityInput && filteredDropSuggestions.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={0.75}>
            {filteredDropSuggestions.map((city) => (
              <Chip key={city} label={city} size="small" variant="outlined"
                onClick={() => addDropCity(city)} sx={{ mb: 0.25 }} />
            ))}
          </Stack>
        )}

        {focusedRouteField !== null && (
          <>
            {/* Popular cities */}
            <Box mt={1.25}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>Popular cities</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={0.5}>
                {popularCitySuggestions.map((city) => (
                  <Chip key={city} label={city} size="small" variant="outlined"
                    onClick={() => handlePopularCityClick(city)} sx={{ mb: 0.25 }} />
                ))}
                {!showMoreCities && (
                  <Chip label="+More" size="small" variant="outlined"
                    onClick={() => setShowMoreCities(true)} sx={{ mb: 0.25 }} />
                )}
              </Stack>
            </Box>

            {token && preferences.serviceAreas.filter((a) => !pickupAreas.includes(a.name) && !dropAreas.includes(a.name)).length > 0 && (
              <Box mt={0.75}>
                <Typography variant="caption" color="text.secondary">Your saved areas</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={0.5}>
                  {preferences.serviceAreas
                    .filter((a) => !pickupAreas.includes(a.name) && !dropAreas.includes(a.name))
                    .map((area) => (
                      <Chip key={area.name} label={area.name} size="small" color="secondary" variant="outlined"
                        onClick={() => handlePopularCityClick(area.name)} />
                    ))}
                </Stack>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* ── Vehicle type ──────────────────────────────────────────────── */}
      <Box py={1.5}>
        <Typography variant="subtitle2" fontWeight={700} mb={1}>Vehicle type</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mb={showVehicleInput ? 1 : 0}>
          {VEHICLE_TYPES.map(({ value, label }) => {
            const active = vehicleFilters.includes(value);
            return (
              <Chip key={value} label={label} size="small"
                color={active ? 'primary' : 'default'} variant={active ? 'filled' : 'outlined'}
                onClick={() => setVehicleFilters(toggleInList(vehicleFilters, value))}
                sx={{ mb: 0.5 }}
              />
            );
          })}
          {vehicleFilters.filter((v) => !VEHICLE_TYPES.find((t) => t.value === v)).map((v) => (
            <Chip key={v} label={v} size="small" color="primary"
              onDelete={() => setVehicleFilters(vehicleFilters.filter((x) => x !== v))}
              sx={{ mb: 0.5 }}
            />
          ))}
          {!showVehicleInput && (
            <Chip label="+ Add more" size="small" variant="outlined"
              onClick={() => setShowVehicleInput(true)}
              sx={{ mb: 0.5, color: 'text.secondary', borderStyle: 'dashed' }}
            />
          )}
        </Stack>
        {showVehicleInput && (
          <TextField
            value={vehicleCustomInput}
            onChange={(e) => setVehicleCustomInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCustomVehicle()}
            placeholder="Add custom vehicle type…"
            size="small"
            fullWidth
            autoFocus
            sx={{ '& input': { fontSize: 12 } }}
            InputProps={{
              endAdornment: vehicleCustomInput
                ? <Button size="small" sx={{ minWidth: 0, px: 1 }} onClick={addCustomVehicle}>Add</Button>
                : <IconButton size="small" onClick={() => { setShowVehicleInput(false); setVehicleCustomInput(''); }} sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>,
            }}
          />
        )}
      </Box>

      {/* ── Trip type ─────────────────────────────────────────────────── */}
      <Box py={1.5}>
        <Typography variant="subtitle2" fontWeight={700} mb={1}>Trip type</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mb={showTripInput ? 1 : 0}>
          {DUTY_TYPES.map(({ value, label }) => {
            const active = dutyTypes.includes(value);
            return (
              <Chip key={value} label={label} size="small"
                color={active ? 'primary' : 'default'} variant={active ? 'filled' : 'outlined'}
                onClick={() => setDutyTypes(toggleInList(dutyTypes, value))}
                sx={{ mb: 0.5 }}
              />
            );
          })}
          {dutyTypes.filter((v) => !DUTY_TYPES.find((t) => t.value === v)).map((v) => (
            <Chip key={v} label={v} size="small" color="primary"
              onDelete={() => setDutyTypes(dutyTypes.filter((x) => x !== v))}
              sx={{ mb: 0.5 }}
            />
          ))}
          {!showTripInput && (
            <Chip label="+ Add more" size="small" variant="outlined"
              onClick={() => setShowTripInput(true)}
              sx={{ mb: 0.5, color: 'text.secondary', borderStyle: 'dashed' }}
            />
          )}
        </Stack>
        {showTripInput && (
          <TextField
            value={dutyCustomInput}
            onChange={(e) => setDutyCustomInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCustomDuty()}
            placeholder="Add custom trip type…"
            size="small"
            fullWidth
            autoFocus
            sx={{ '& input': { fontSize: 12 } }}
            InputProps={{
              endAdornment: dutyCustomInput
                ? <Button size="small" sx={{ minWidth: 0, px: 1 }} onClick={addCustomDuty}>Add</Button>
                : <IconButton size="small" onClick={() => { setShowTripInput(false); setDutyCustomInput(''); }} sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>,
            }}
          />
        )}
      </Box>

      {/* ── Priority ──────────────────────────────────────────────────── */}
      <Box py={1.5}>
        <Typography variant="subtitle2" fontWeight={700} mb={1}>Priority</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {PRIORITY_OPTIONS.map(({ value, label }) => {
            const active = priorities.includes(value);
            return (
              <Chip
                key={value}
                label={label}
                size="small"
                color={active ? (value === 'high' ? 'error' : 'primary') : 'default'}
                variant={active ? 'filled' : 'outlined'}
                onClick={() => setPriorities(toggleInList(priorities, value))}
                sx={{ mb: 0.5 }}
              />
            );
          })}
        </Stack>
      </Box>

      {/* ── More filters ──────────────────────────────────────────────── */}
      <Box py={1.5}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.25}>More filters</Typography>
        <Stack spacing={1.5}>
          {([
            { label: 'Only recent rides', subtext: 'Created in the last hour', checked: onlyRecentFilter, onChange: setOnlyRecentFilter },
            { label: 'Hide expired', subtext: 'Show only active rides', checked: hideExpiredFilter, onChange: setHideExpiredFilter },
            { label: 'Urgent only', subtext: 'Posted within 20 minutes', checked: urgentOnlyFilter, onChange: (v: boolean) => setUrgentOnlyFilter(v) },
            { label: 'Show Exchange Duty', subtext: 'Include exchange duty rides', checked: showExchangeDutyFilter, onChange: setShowExchangeDutyFilter },
            { label: 'Show vehicle availability', subtext: 'Include drivers advertising free vehicles', checked: showSupplyFilter, onChange: setShowSupplyFilter },
          ] as Array<{ label: string; subtext: string; checked: boolean; onChange: (v: boolean) => void }>).map(({ label, subtext, checked, onChange }) => (
            <Box key={label} display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                {checked
                  ? <CheckBoxIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  : <CheckBoxOutlineBlankIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                }
                <Box>
                  <Typography variant="body2" lineHeight={1.3}>{label}</Typography>
                  <Typography variant="caption" color="text.secondary">{subtext}</Typography>
                </Box>
              </Box>
              <Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />
            </Box>
          ))}
        </Stack>
      </Box>

    </Stack>
  );

  return (
    <Stack spacing={0} sx={{ maxWidth: 1280, mx: 'auto', px: inDashboard ? 0 : { xs: 2, md: 3 }, py: inDashboard ? 0 : { xs: 2, md: 3 } }}>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg,#061b31,#0b3156)',
          color: 'white',
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
        >
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: 20, md: 24 } }}>
              {token ? 'Driver Workspace' : 'Live Ride Requests'}
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.72)" mt={0.5} sx={{ display: { xs: 'none', sm: 'block' } }}>
              {token
                ? 'Find high-quality rides and grow your earnings.'
                : freeMode
                  ? 'Browse live rides and contact riders directly while Free Rides Mode is active.'
                  : 'Browse active ride requirements. Sign in to unlock full ride details.'}
            </Typography>
          </Box>
          {token && entitlements ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                bgcolor: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,201,40,0.3)',
                borderRadius: 2,
                px: 2,
                py: 1,
                flexShrink: 0,
                cursor: 'pointer',
              }}
              onClick={() => navigate('/wallet')}
            >
              <StarIcon sx={{ color: '#ffc928', fontSize: 18 }} />
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight={700} color="white" sx={{ fontSize: 13 }}>
                    {entitlements.planName}
                  </Typography>
                  <Box sx={{ px: 0.75, py: 0.1, bgcolor: '#14a35b', borderRadius: 10, fontSize: 10, fontWeight: 800, color: 'white', lineHeight: '18px' }}>
                    Active
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>
                  {entitlements.monthlyLeadLimit === null ? 'Unlimited unlocks' : `${entitlements.remaining ?? 0} unlocks remaining`}
                </Typography>
              </Box>
              <ChevronRightIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 16 }} />
            </Box>
          ) : !token && !freeMode ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/signup')}
              sx={{ bgcolor: '#ffc928', color: '#07182d', fontWeight: 800, flexShrink: 0, '&:hover': { bgcolor: '#f3b51b' } }}
            >
              Sign up free
            </Button>
          ) : null}
        </Box>
      </Paper>

      {/* ── Stats row (auth only) ─────────────────────────────────────────── */}
      {token && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            mb: 2,
            overflowX: 'auto',
            pb: 0.5,
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {[
            {
              icon: <LocalOfferIcon sx={{ fontSize: 18, color: '#08233f' }} />,
              label: 'Available Rides',
              value: loading ? '—' : totalRides.toLocaleString(),
              sub: pendingCount > 0 ? `+${pendingCount} new` : null,
              subColor: '#14a35b',
              bg: 'rgba(8,35,63,0.04)',
              borderColor: 'rgba(8,35,63,0.12)',
            },
            {
              icon: <LockOpenIcon sx={{ fontSize: 18, color: '#14a35b' }} />,
              label: 'Unlocks Left',
              value: entitlements ? (entitlements.remaining === null ? '∞' : String(entitlements.remaining)) : '—',
              sub: entitlements?.monthlyLeadLimit === null ? 'Unlimited' : null,
              subColor: '#14a35b',
              bg: 'rgba(20,163,91,0.04)',
              borderColor: 'rgba(20,163,91,0.15)',
            },
            {
              icon: <BoltIcon sx={{ fontSize: 18, color: '#f3b51b' }} />,
              label: 'Urgent Now',
              value: loading ? '—' : String(urgentCount),
              sub: urgentCount > 0 ? 'Need attention' : 'None right now',
              subColor: urgentCount > 0 ? '#c62828' : '#94a3b8',
              bg: urgentCount > 0 ? 'rgba(198,40,40,0.04)' : 'rgba(0,0,0,0.02)',
              borderColor: urgentCount > 0 ? 'rgba(198,40,40,0.2)' : 'rgba(0,0,0,0.08)',
            },
          ].map((stat) => (
            <Paper
              key={stat.label}
              variant="outlined"
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                bgcolor: stat.bg,
                borderColor: stat.borderColor,
                minWidth: { xs: 130, sm: 'auto' },
                flex: { sm: 1 },
                flexShrink: 0,
              }}
            >
              <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
                {stat.icon}
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                  {stat.label}
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: 22, sm: 26 }, lineHeight: 1 }}>
                {stat.value}
              </Typography>
              {stat.sub && (
                <Typography variant="caption" sx={{ color: stat.subColor, fontSize: 11 }}>
                  {stat.sub}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {loadError && (
        <Alert severity="error" onClose={() => setLoadError(null)} sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => resetAndLoad()}>Retry</Button>}>
          {loadError}
        </Alert>
      )}

      {/* ── Ride tabs (auth only) ─────────────────────────────────────────── */}
      {token && (
        <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={rideTab}
            onChange={changeRideTab}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: '1px solid rgba(7,24,45,0.08)', minHeight: 44 }}
          >
            <Tab value="best" label="Recommended ⭐" sx={{ minHeight: 44, fontSize: { xs: 11, sm: 13 } }} />
            <Tab
              value="all"
              label={
                <Box component="span" display="flex" alignItems="center" gap={0.5}>
                  All Rides
                  {urgentCount > 0 && (
                    <Box
                      component="span"
                      sx={{
                        px: 0.75,
                        bgcolor: '#fff0ee',
                        color: '#c62828',
                        borderRadius: 10,
                        fontSize: 10,
                        fontWeight: 800,
                        border: '1px solid rgba(198,40,40,0.3)',
                        lineHeight: '18px',
                      }}
                    >
                      ⚡{urgentCount}
                    </Box>
                  )}
                </Box>
              }
              sx={{ minHeight: 44, fontSize: { xs: 11, sm: 13 } }}
            />
            <Tab value="saved" label="Saved" sx={{ minHeight: 44, fontSize: { xs: 11, sm: 13 } }} />
            <Tab value="hidden" label="Hidden" sx={{ minHeight: 44, fontSize: { xs: 11, sm: 13 } }} />
            <Tab value="unlocked" label="Unlocked" sx={{ minHeight: 44, fontSize: { xs: 11, sm: 13 } }} />
          </Tabs>
          {rideTab === 'best' && (
            <Box px={2} py={0.75} bgcolor="rgba(255,201,40,0.06)">
              <Typography variant="caption" color="text.secondary">
                {personalizedCount > 0
                  ? `${personalizedCount} rides match your preferences`
                  : 'No preference matches on this page — try All Rides.'}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* ── Main layout ───────────────────────────────────────────────────── */}
      <Grid container spacing={2.5} alignItems="flex-start">

        {/* Sidebar — desktop only */}
        <Grid item md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              position: 'sticky',
              top: inDashboard ? 80 : 88,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: `calc(100vh - ${inDashboard ? 100 : 108}px)`,
            }}
          >
            {/* Header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px={2}
              py={1.5}
              sx={{ borderBottom: '1px solid rgba(7,24,45,0.08)', bgcolor: 'rgba(7,24,45,0.02)', flexShrink: 0 }}
            >
              <Typography variant="subtitle2" fontWeight={800}>Filters</Typography>
              <Stack direction="row" spacing={0.5}>
                {hasActiveFilters && (
                  <Button size="small" variant="text" onClick={clearAllFilters}
                    sx={{ fontSize: 11, py: 0, px: 0.75, color: 'text.secondary', minWidth: 0 }}>
                    Clear all
                  </Button>
                )}
                <Button size="small" variant="text" onClick={clearAllFilters}
                  sx={{ fontSize: 11, py: 0, px: 0.75, color: 'text.secondary', minWidth: 0 }}>
                  Reset
                </Button>
              </Stack>
            </Box>
            {/* Scrollable body */}
            <Box px={2} sx={{ flex: 1, overflowY: 'auto' }}>
              {filterContent}
            </Box>
            {/* Sticky footer */}
            <Box px={2} py={1.5} sx={{ borderTop: '1px solid rgba(7,24,45,0.08)', flexShrink: 0 }}>
              {token && presets.length > 0 && (
                <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                  <Select
                    value={selectedPresetId}
                    onChange={(e) => handleSelectPreset(e.target.value)}
                    displayEmpty
                    sx={{ fontSize: 12 }}
                  >
                    <MenuItem value="" sx={{ fontSize: 12 }}>Select saved filter…</MenuItem>
                    {presets.map((p) => (
                      <MenuItem key={p.id} value={p.id} sx={{ fontSize: 12 }}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {token && (
                <Box display="flex" gap={0.75}>
                  <TextField
                    value={savePresetName}
                    onChange={(e) => setSavePresetName(e.target.value.slice(0, 40))}
                    onKeyDown={(e) => e.key === 'Enter' && savePresetName.trim() && handleSaveFilter()}
                    placeholder={selectedPreset ? `Rename "${selectedPreset.name}"` : 'Filter name…'}
                    size="small"
                    sx={{ flex: 1, '& input': { fontSize: 12 } }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!savePresetName.trim() || savingPreset || updatingPreset}
                    onClick={handleSaveFilter}
                    sx={{ flexShrink: 0, bgcolor: '#07182d', color: 'white', fontSize: 12, py: 0.875, px: 1.5, textTransform: 'none', '&:hover': { bgcolor: '#0b2e50' } }}
                  >
                    {savingPreset || updatingPreset ? '…' : selectedPreset ? 'Update' : 'Save'}
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Rides content */}
        <Grid item xs={12} md={9}>

          {/* Mobile: filter bar */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, mb: 2, alignItems: 'center', overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setFilterDrawerOpen(true)}
              sx={{ flexShrink: 0, borderRadius: 5 }}
            >
              Filters
              {hasActiveFilters && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.5, width: 16, height: 16, borderRadius: '50%',
                    bgcolor: 'secondary.main', color: '#07182d',
                    fontSize: 10, fontWeight: 800,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {[
                    search ? 1 : 0,
                    vehicleFilters.length ? 1 : 0,
                    dutyTypes.length ? 1 : 0,
                    priorities.length ? 1 : 0,
                    pickupAreas.length ? 1 : 0,
                    dropAreas.length ? 1 : 0,
                    urgentOnlyFilter ? 1 : 0,
                  ].reduce<number>((a, b) => a + b, 0)}
                </Box>
              )}
            </Button>
            {search && <Chip size="small" label={`"${search}"`} onDelete={() => setSearch('')} sx={{ flexShrink: 0 }} />}
            {pickupAreas.length > 0 && <Chip size="small" label={`From: ${pickupAreas.join(', ')}`} onDelete={() => setPickupAreas([])} sx={{ flexShrink: 0 }} />}
            {dropAreas.length > 0 && <Chip size="small" label={`To: ${dropAreas.join(', ')}`} onDelete={() => setDropAreas([])} sx={{ flexShrink: 0 }} />}
            {vehicleFilters.length > 0 && (
              <Chip
                size="small"
                label={`Vehicle: ${vehicleFilters.join(', ')}`}
                onDelete={() => setVehicleFilters([])}
                sx={{ flexShrink: 0, textTransform: 'capitalize' }}
              />
            )}
            {dutyTypes.length > 0 && (
              <Chip
                size="small"
                label={`Duty: ${dutyTypes.join(', ')}`}
                onDelete={() => setDutyTypes([])}
                sx={{ flexShrink: 0, textTransform: 'capitalize' }}
              />
            )}
            {priorities.length > 0 && (
              <Chip
                size="small"
                label={`Priority: ${priorities.join(', ')}`}
                onDelete={() => setPriorities([])}
                sx={{ flexShrink: 0, textTransform: 'capitalize' }}
              />
            )}
            {urgentOnlyFilter && <Chip size="small" label="Urgent only" color="error" variant="outlined" onDelete={() => setUrgentOnlyFilter(false)} sx={{ flexShrink: 0 }} />}
          </Box>

          {/* New rides banner */}
          {pendingCount > 0 && (
            <Button
              fullWidth
              variant="outlined"
              onClick={handleLoadNew}
              sx={{
                mb: 2, py: 1.25,
                borderColor: '#ffc928',
                bgcolor: '#fffaf0',
                color: '#07182d',
                fontWeight: 600,
                fontSize: 14,
                textTransform: 'none',
                '&:hover': { bgcolor: '#fff2c2', borderColor: '#d99b00' },
              }}
            >
              {pendingCount} new ride{pendingCount !== 1 ? 's' : ''} available — click to load
            </Button>
          )}

          {/* Sort + view toolbar */}
          {!loading && displayedRides.length > 0 && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1.5}
              flexWrap="wrap"
              gap={1}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {displayedRides.length}
                {(totalRides > rides.length || urgentOnlyFilter)
                  ? ` of ${totalRides}`
                  : ''} ride{totalRides !== 1 ? 's' : ''}
                {!token && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    · {freeMode ? 'Contacts shown free' : 'Sign in to unlock rides'}
                  </Typography>
                )}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {urgentCount > 0 && !urgentOnlyFilter && (
                  <Tooltip title="Show only urgent rides">
                    <Chip
                      label={`⚡ ${urgentCount} urgent`}
                      size="small"
                      variant="outlined"
                      onClick={() => setUrgentOnlyFilter(true)}
                      sx={{ fontSize: 11, color: '#c62828', borderColor: 'rgba(198,40,40,0.4)', cursor: 'pointer' }}
                    />
                  </Tooltip>
                )}
                {/* View toggle */}
                <Box sx={{ display: 'flex', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, overflow: 'hidden' }}>
                  <Tooltip title="Grid view">
                    <IconButton
                      size="small"
                      onClick={() => setViewMode('grid')}
                      sx={{
                        borderRadius: 0,
                        bgcolor: viewMode === 'grid' ? 'rgba(8,35,63,0.08)' : 'transparent',
                        px: 0.75,
                      }}
                    >
                      <GridViewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="List view">
                    <IconButton
                      size="small"
                      onClick={() => setViewMode('list')}
                      sx={{
                        borderRadius: 0,
                        bgcolor: viewMode === 'list' ? 'rgba(8,35,63,0.08)' : 'transparent',
                        px: 0.75,
                      }}
                    >
                      <ViewListIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                {/* Sort by */}
                <FormControl size="small">
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as RidesSortBy)}
                    displayEmpty
                    sx={{ fontSize: 13, height: 32, minWidth: 140 }}
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : displayedRides.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="body1" fontWeight={600} gutterBottom>No rides found</Typography>
              <Typography variant="body2" color="text.secondary">
                {hasActiveFilters
                  ? 'Try clearing your filters.'
                  : rideTab === 'saved' ? 'You have no saved rides yet.'
                  : rideTab === 'hidden' ? 'You have no hidden rides.'
                  : rideTab === 'unlocked' ? 'You have not unlocked any rides yet.'
                  : 'No active rides right now. Check back soon.'}
              </Typography>
              {hasActiveFilters && (
                <Button size="small" sx={{ mt: 1.5 }} onClick={clearAllFilters}>
                  Clear filters
                </Button>
              )}
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {displayedRides.map((ride) => (
                <Grid key={ride.id} item xs={12} sm={viewMode === 'list' ? 12 : 6}>
                  <RideCard
                    ride={ride}
                    unlocked={unlocked[ride.id]}
                    onUnlock={freeMode ? undefined : () => handleUnlockLead(ride)}
                    unlockDisabled={unlockingId === ride.id || !isVerified}
                    onSave={token && rideTab !== 'hidden' ? () => handleSaveLead(ride.id) : undefined}
                    onUnsave={token && rideTab !== 'hidden' ? () => handleUnsaveLead(ride.id) : undefined}
                    onHide={token && rideTab !== 'hidden' ? () => handleHideLead(ride.id) : undefined}
                    onUnhide={token && rideTab === 'hidden' ? () => handleUnhideLead(ride.id) : undefined}
                    onReport={token ? () => handleReportLead(ride.id) : undefined}
                    onDetails={() => setSelectedRide(ride)}
                    actionDisabled={actionWorkingId === ride.id}
                    creditsRemaining={entitlements?.remaining ?? null}
                    freeAccess={freeMode}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Infinite scroll sentinel */}
          <Box ref={sentinelRef} sx={{ height: 32 }} />
          {loadingMore && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!loading && !loadingMore && !hasMore && rides.length > 0 && (
            <Typography variant="caption" color="text.secondary" textAlign="center" display="block" py={2}>
              All {totalRides} ride{totalRides !== 1 ? 's' : ''} loaded
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* ── Mobile filter drawer ───────────────────────────────────────────── */}
      <Drawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px 16px 0 0', maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}
      >
        {/* Fixed header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2.5}
          pt={2}
          pb={1.5}
          sx={{ borderBottom: '1px solid rgba(7,24,45,0.08)', flexShrink: 0 }}
        >
          <Typography variant="subtitle1" fontWeight={800}>Filters</Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {hasActiveFilters && (
              <Button size="small" variant="text" onClick={clearAllFilters}
                sx={{ fontSize: 12, py: 0, color: 'text.secondary' }}>
                Clear all
              </Button>
            )}
            <IconButton size="small" onClick={() => setFilterDrawerOpen(false)}><CloseIcon /></IconButton>
          </Stack>
        </Box>
        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5 }}>
          {filterContent}
        </Box>
        {/* Sticky footer */}
        <Box px={2.5} py={1.5} sx={{ borderTop: '1px solid rgba(7,24,45,0.08)', flexShrink: 0 }}>
          {token && presets.length > 0 && (
            <FormControl size="small" fullWidth sx={{ mb: 1 }}>
              <Select
                value={selectedPresetId}
                onChange={(e) => handleSelectPreset(e.target.value)}
                displayEmpty
                sx={{ fontSize: 12 }}
              >
                <MenuItem value="" sx={{ fontSize: 12 }}>Select saved filter…</MenuItem>
                {presets.map((p) => (
                  <MenuItem key={p.id} value={p.id} sx={{ fontSize: 12 }}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box display="flex" gap={0.75}>
            {token && (
              <TextField
                value={savePresetName}
                onChange={(e) => setSavePresetName(e.target.value.slice(0, 40))}
                onKeyDown={(e) => e.key === 'Enter' && savePresetName.trim() && handleSaveFilter()}
                placeholder={selectedPreset ? `Rename "${selectedPreset.name}"` : 'Filter name…'}
                size="small"
                sx={{ flex: 1, '& input': { fontSize: 12 } }}
              />
            )}
            {token && (
              <Button
                variant="contained"
                size="small"
                disabled={!savePresetName.trim() || savingPreset || updatingPreset}
                onClick={handleSaveFilter}
                sx={{ flexShrink: 0, bgcolor: '#07182d', color: 'white', fontSize: 12, py: 0.875, px: 1.5, textTransform: 'none', '&:hover': { bgcolor: '#0b2e50' } }}
              >
                {savingPreset || updatingPreset ? '…' : selectedPreset ? 'Update' : 'Save'}
              </Button>
            )}
            <Button
              size="small"
              onClick={() => setFilterDrawerOpen(false)}
              sx={{ flexShrink: 0, fontSize: 12, py: 0.875, px: 1.5, textTransform: 'none', color: 'text.secondary' }}
            >
              Done
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ── Lead detail drawer ─────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={Boolean(selectedRide)}
        onClose={() => setSelectedRide(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, display: 'flex', flexDirection: 'column' } }}
      >
        {selectedRide && (
          <>
            {/* Fixed header */}
            <Box sx={{ px: 2.5, pt: 2.25, pb: 1.5, flexShrink: 0 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography fontWeight={700} sx={{ fontSize: 20, color: '#111', lineHeight: 1.3 }}>Lead details</Typography>
                  <Typography sx={{ fontSize: 12, color: '#888', mt: 0.25 }}>
                    {new Date(selectedRide.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                <IconButton onClick={() => setSelectedRide(null)} sx={{ mt: -0.5, mr: -0.5 }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, pb: 2 }}>
              <Stack spacing={1.75}>

                {/* Route */}
                <Box sx={{ bgcolor: '#F5F5F0', borderRadius: 2, p: 2 }}>
                  <Typography sx={{ fontSize: 10, color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
                    Route
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2E7D32', flexShrink: 0 }} />
                    <Typography fontWeight={700} sx={{ fontSize: 22, color: '#111', lineHeight: 1.2 }}>
                      {selectedRide.from || 'Pickup'}
                    </Typography>
                    <Typography sx={{ color: '#bbb', fontSize: 16, lineHeight: 1 }}>→</Typography>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#C62828', flexShrink: 0 }} />
                    <Typography fontWeight={700} sx={{ fontSize: 22, color: '#111', lineHeight: 1.2 }}>
                      {selectedRide.to || 'Drop'}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, color: '#555', mt: 0.5 }}>
                    {selectedRide.time || 'Flexible time'}
                  </Typography>
                </Box>

                {/* Chips */}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={`Priority: ${selectedRide.priority}`}
                    size="small"
                    variant="outlined"
                    sx={selectedRide.priority === 'high'
                      ? { fontSize: 12, height: 26, color: '#E65100', borderColor: '#FFB74D', borderRadius: '20px' }
                      : { fontSize: 12, height: 26, color: '#555', borderColor: '#DDD', borderRadius: '20px' }
                    }
                  />
                  {selectedRide.vehicleType && (
                    <Chip label={selectedRide.vehicleType} size="small" variant="outlined"
                      sx={{ fontSize: 12, height: 26, color: '#555', borderColor: '#DDD', borderRadius: '20px', textTransform: 'capitalize' }} />
                  )}
                  {selectedRide.dutyType && (
                    <Chip
                      label={({ 'one-way': 'One Way', 'oneway': 'One Way', 'round-trip': 'Round Trip', 'roundtrip': 'Round Trip', 'outstation': 'Outstation', 'local': 'Local' } as Record<string, string>)[selectedRide.dutyType.toLowerCase()] ?? selectedRide.dutyType}
                      size="small" variant="outlined"
                      sx={{ fontSize: 12, height: 26, color: '#555', borderColor: '#DDD', borderRadius: '20px' }} />
                  )}
                  {selectedRide.match?.isMatch && (
                    <Chip label={`Match ${selectedRide.match.score}%`} size="small" variant="outlined"
                      sx={{ fontSize: 12, height: 26, color: '#14a35b', borderColor: 'rgba(20,163,91,0.35)', borderRadius: '20px' }} />
                  )}
                </Stack>

                {/* Contact & price card */}
                <Box sx={{ border: '1px solid #E5E5E5', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ px: 1.75, pt: 1.5, pb: 1.25 }}>
                    <Typography fontWeight={600} sx={{ fontSize: 13, color: '#111', mb: 1.25 }}>Ride details & connection</Typography>
                    {unlocked[selectedRide.id]?.contactNumber ? (
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <Avatar sx={{ bgcolor: '#2E7D32', width: 32, height: 32, flexShrink: 0 }}>
                          <PersonIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box>
                          <Typography fontWeight={600} sx={{ fontSize: 13, color: '#111' }}>
                            {unlocked[selectedRide.id]?.contactName || 'Rider'}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: '#2E7D32' }}>
                            {unlocked[selectedRide.id]?.contactNumber}
                          </Typography>
                        </Box>
                      </Box>
                    ) : freeMode ? (
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <Avatar sx={{ bgcolor: '#757575', width: 32, height: 32, flexShrink: 0 }}>
                          <PersonIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Typography sx={{ fontSize: 13, color: '#555' }}>
                          Contact not available for this ride.
                        </Typography>
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="flex-start" gap={1.25}>
                        <Avatar sx={{ bgcolor: '#424242', width: 32, height: 32, flexShrink: 0, mt: 0.25 }}>
                          <LockIcon sx={{ fontSize: 15, color: 'white' }} />
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontSize: 13, color: '#111', lineHeight: 1.4 }}>
                            1 credit required to unlock this ride.
                          </Typography>
                          {entitlements?.remaining !== undefined && entitlements.remaining !== null && (
                            <Typography sx={{ fontSize: 12, color: '#E8A000', mt: 0.375 }}>
                              🪙 {entitlements.remaining} credits remaining
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Divider />
                  {unlocked[selectedRide.id]?.contactNumber ? (
                    <ButtonBase
                      onClick={() => { const n = unlocked[selectedRide.id]?.contactNumber; if (n) window.location.href = `tel:${n}`; }}
                      sx={{ width: '100%', px: 1.75, py: 1.25, display: 'flex', alignItems: 'center', gap: 0.75, '&:hover': { bgcolor: '#F0FAF0' } }}
                    >
                      <PhoneIcon sx={{ fontSize: 15, color: '#2E7D32' }} />
                      <Typography fontWeight={600} sx={{ fontSize: 13, color: '#2E7D32' }}>
                        {(() => {
                          const p = unlocked[selectedRide.id]?.price;
                          if (!p) return 'Call';
                          return String(p).match(/^\d/) ? `₹${p}` : p;
                        })()}
                      </Typography>
                    </ButtonBase>
                  ) : freeMode ? null : (
                    <ButtonBase
                      onClick={() => handleUnlockLead(selectedRide)}
                      disabled={unlockingId === selectedRide.id || !isVerified}
                      sx={{ width: '100%', px: 1.75, py: 1.25, display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: '#FFFBF0', '&:hover': { bgcolor: '#FFF3D0' } }}
                    >
                      <LockIcon sx={{ fontSize: 14, color: '#E8A000' }} />
                      <Typography fontWeight={600} sx={{ fontSize: 13, color: '#E8A000' }}>
                        Unlock Ride · View full details & connect
                      </Typography>
                    </ButtonBase>
                  )}
                </Box>

                {/* Original message */}
                {(unlocked[selectedRide.id]?.originalMessage || selectedRide.maskedMessage) && (
                  <Box sx={{ border: '1px solid #E5E5E5', borderRadius: 2, overflow: 'hidden' }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 1.75, pt: 1.25, pb: 0.75 }}>
                      <Typography sx={{ fontSize: 10, color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Original Message
                      </Typography>
                      {!unlocked[selectedRide.id]?.contactNumber && !freeMode && (
                        <Box display="flex" alignItems="center" gap={0.375}>
                          <LockIcon sx={{ fontSize: 11, color: '#E8A000' }} />
                          <Typography sx={{ fontSize: 11, color: '#E8A000' }}>details locked</Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ borderLeft: '3px solid', borderColor: unlocked[selectedRide.id]?.contactNumber || freeMode ? '#2E7D32' : '#E8A000', mx: 1.75, mb: 1.75 }}>
                      <Typography sx={{ pl: 1.25, fontSize: 13.5, color: '#222', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {tidyOriginalMessage(unlocked[selectedRide.id]?.originalMessage || selectedRide.maskedMessage)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Match reasons */}
                {selectedRide.match?.matchedReasons?.length ? (
                  <Box sx={{ border: '1px solid #E5E5E5', borderRadius: 2, px: 1.75, py: 1.5 }}>
                    <Typography sx={{ fontSize: 10, color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
                      Why this matched
                    </Typography>
                    <Stack spacing={0.5}>
                      {selectedRide.match.matchedReasons.map((r) => (
                        <Typography key={r} sx={{ fontSize: 13, color: '#555' }}>{r}</Typography>
                      ))}
                    </Stack>
                  </Box>
                ) : null}

              </Stack>
            </Box>

            {/* Fixed bottom actions */}
            <Box sx={{ px: 2, py: 1.75, borderTop: '1px solid #EBEBEB', flexShrink: 0 }}>
              {unlocked[selectedRide.id]?.contactNumber ? (
                /* Unlocked: black Save | outlined Hide | Not relevant */
                token ? (
                  <>
                    <Stack direction="row" spacing={1} mb={1}>
                      <Button variant="contained" fullWidth
                        onClick={() => selectedRide.saved ? handleUnsaveLead(selectedRide.id) : handleSaveLead(selectedRide.id)}
                        disabled={actionWorkingId === selectedRide.id}
                        sx={{ bgcolor: '#111', color: 'white', fontWeight: 600, fontSize: 13, py: 0.875, borderRadius: 1.5, '&:hover': { bgcolor: '#333' } }}>
                        {selectedRide.saved ? 'Unsave' : 'Save'}
                      </Button>
                      <Button variant="outlined" fullWidth
                        onClick={() => handleHideLead(selectedRide.id)}
                        disabled={actionWorkingId === selectedRide.id}
                        sx={{ color: '#111', borderColor: '#DDD', fontWeight: 600, fontSize: 13, py: 0.875, borderRadius: 1.5 }}>
                        Hide
                      </Button>
                    </Stack>
                    <Button variant="text" size="small"
                      onClick={() => handleReportLead(selectedRide.id)}
                      disabled={actionWorkingId === selectedRide.id || selectedRide.reported}
                      sx={{ px: 0, fontSize: 13, color: '#E8A000', fontWeight: 500 }}>
                      {selectedRide.reported ? 'Reported' : 'Not relevant'}
                    </Button>
                  </>
                ) : null
              ) : freeMode ? null : (
                /* Locked: amber Unlock | outlined Save + Hide | Not relevant */
                <>
                  <Button variant="contained" fullWidth
                    startIcon={<LockIcon sx={{ fontSize: 15 }} />}
                    onClick={() => handleUnlockLead(selectedRide)}
                    disabled={unlockingId === selectedRide.id || !isVerified}
                    sx={{ bgcolor: '#E8A000', color: 'white', fontWeight: 600, fontSize: 13, py: 0.875, borderRadius: 1.5, mb: token ? 1 : 0, '&:hover': { bgcolor: '#d97706' }, '&.Mui-disabled': { bgcolor: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.26)' } }}>
                    Unlock Ride
                  </Button>
                  {token && (
                    <>
                      <Stack direction="row" spacing={1} mb={1}>
                        <Button variant="outlined" fullWidth
                          onClick={() => selectedRide.saved ? handleUnsaveLead(selectedRide.id) : handleSaveLead(selectedRide.id)}
                          disabled={actionWorkingId === selectedRide.id}
                          sx={{ color: '#111', borderColor: '#DDD', fontWeight: 600, fontSize: 13, py: 0.875, borderRadius: 1.5 }}>
                          {selectedRide.saved ? 'Unsave' : 'Save'}
                        </Button>
                        <Button variant="outlined" fullWidth
                          onClick={() => handleHideLead(selectedRide.id)}
                          disabled={actionWorkingId === selectedRide.id}
                          sx={{ color: '#111', borderColor: '#DDD', fontWeight: 600, fontSize: 13, py: 0.875, borderRadius: 1.5 }}>
                          Hide
                        </Button>
                      </Stack>
                      <Button variant="text" size="small"
                        onClick={() => handleReportLead(selectedRide.id)}
                        disabled={actionWorkingId === selectedRide.id || selectedRide.reported}
                        sx={{ px: 0, fontSize: 13, color: '#E8A000', fontWeight: 500 }}>
                        {selectedRide.reported ? 'Reported' : 'Not relevant'}
                      </Button>
                    </>
                  )}
                </>
              )}
            </Box>
          </>
        )}
      </Drawer>

      <Snackbar open={Boolean(message)} autoHideDuration={3000} onClose={() => setMessage(null)} message={message} />
    </Stack>
  );
};

export default PublicRides;
